import { Snackbar } from '@xo-union/tk-component-alerts';
import { Subhead } from '@xo-union/tk-ui-typography';
import honeybadger from 'honeybadger-js';
import React, { useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useUpdateThemeMutation, useWeddingWebsiteQuery } from '../../generated/graphql';
import { changeThemeRoute } from '../../helpers/routes';
import { DISMISS_TIMEOUT, SnackbarInterface } from '../../helpers/snackbar';
import Loading from '../../Loading';
import { TrackingContext } from '../../TrackingContext';
import VariantContext from '../../VariantContext';
import LinkButton from '../LinkButton';
import { lookupTheme, lookupThemeFamily } from '../PreviewPage/ChangeThemeRail';
import { updateThemeSuccessCallback } from '../PreviewPage/LiteSitePreview';
import MatchingPaperInvitations from './MatchingPaperInvitations';
import styles from './themeSettings.scss';
import { RightContainerDivider } from '../RightContainer';
import ThemeCardFullDisplay from '../PreviewPage/ThemeCardFullDisplay';
import useBuildThemeAnalyticsBaseProps from '../../hooks/useBuildThemeAnalyticsBaseProps';

interface ThemeSettingsProps {
  children?: React.ReactNode;
  hideSeparator?: boolean;
}

export const ThemeSettings = ({ hideSeparator = false, children }: ThemeSettingsProps) => {
  const history = useHistory();
  const { loading: themesLoading, themes } = useContext(VariantContext);
  const { data: wwsData, loading: wwsLoading } = useWeddingWebsiteQuery();
  const [updateTheme, { loading: updatingTheme }] = useUpdateThemeMutation();
  const { track } = useContext(TrackingContext);
  const { getThemeAnalyticsBaseProps } = useBuildThemeAnalyticsBaseProps();

  const themeId = wwsData?.weddingWebsite.themeId || null;
  const themeFamily = lookupThemeFamily(themeId, themes);
  const theme = lookupTheme(themeId, themes);
  const snackbar = useRef<SnackbarInterface>();
  const paper = theme?.stationaryPreviewImage?.mediaApiId;

  const onClickSwatch = async (newThemeId: number) => {
    if (updatingTheme) {
      return;
    }
    const previousTheme = lookupTheme(themeId, themes);

    try {
      await updateTheme({
        variables: {
          id: newThemeId,
        },
        update(cache, { data }) {
          updateThemeSuccessCallback(cache, data, newThemeId);
        },
      }).then(() => {
        if (snackbar?.current) {
          snackbar.current.showAndDismiss({
            data: 'Website color updated!',
            dismissTimeout: DISMISS_TIMEOUT,
          });
        }
        const newTheme = lookupTheme(newThemeId, themes || []);
        track('Design Selection', {
          ...getThemeAnalyticsBaseProps(newTheme),
          page: 'dashboard',
          previousTheme: previousTheme?.name,
          colorwayUpdate: true,
        });
      });
    } catch (error) {
      honeybadger.notify(error, 'Error updating theme');
    }
  };

  const navigateToChangeTheme = () => {
    history.push(changeThemeRoute);
    track('WWS Dashboard Interaction', {
      actionName: 'Change Theme',
      action: 'change theme',
    });
  };

  if (themesLoading || wwsLoading) {
    return <Loading />;
  }

  if (!themes || !themes.length || !wwsData?.weddingWebsite) {
    return null;
  }

  const displayThemeCard = () => {
    if (themeFamily?.themes?.length) {
      return (
        <ThemeCardFullDisplay
          themeFamily={themeFamily}
          onClick={onClickSwatch}
          selected={themeId}
          dashboard={true}
          hideInvitationLink={!!paper}
        />
      );
    }
    return null;
  };

  return (
    <>
      <div className={styles.header}>
        <Subhead as="h2" className={styles.subheadMobile}>
          Your theme
        </Subhead>
        <LinkButton onClick={navigateToChangeTheme} color="primary">
          Browse themes
        </LinkButton>
      </div>

      <Snackbar ref={snackbar} />
      {displayThemeCard()}
      {children}
      {paper && theme && themeFamily && (
        <>
          {!hideSeparator && <RightContainerDivider data-testid="theme-and-paper-separator" includeMargin={false} />}
          <MatchingPaperInvitations paper={paper} theme={theme} />
        </>
      )}
    </>
  );
};
